import { useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { fmtDateNumeric, parseNumericDate } from '@siva/shared';
import { t } from '../i18n/index.js';

type Props = {
  /** 'YYYY-MM-DD' or '' */
  value: string;
  onChange: (ymd: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  id?: string;
  /** Accessible name when the field is not wrapped in a <label>. */
  label?: string;
  className?: string;
};

/**
 * Date picker that always shows DD/MM/YYYY whatever the browser locale: a text field (typing
 * accepts 11/09/2026, 11-9-2026 or 2026-09-11) plus a calendar button that opens the native
 * picker of a visually hidden <input type="date"> bound to the same value.
 */
export function DateField({ value, onChange, min, max, required, id, label, className }: Props) {
  const native = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(value ? fmtDateNumeric(value) : '');
  useEffect(() => {
    setText(value ? fmtDateNumeric(value) : '');
  }, [value]);

  const inRange = (ymd: string) => (!min || ymd >= min) && (!max || ymd <= max);
  const commit = (raw: string) => {
    const ymd = parseNumericDate(raw);
    if (ymd && inRange(ymd) && ymd !== value) onChange(ymd);
  };
  /**
   * Chrome opens its picker only from the calendar glyph, so a tap anywhere on the field needs
   * showPicker. iOS has no showPicker for a date input and does not need one: the tap has already
   * landed on a real date field, and it opens its own picker on its own. Throwing here is the
   * normal path on a phone, not an error.
   */
  const openPicker = (el: HTMLInputElement) => {
    try {
      el.showPicker();
    } catch {
      /* iOS and older Safari: the tap itself opens the picker */
    }
  };

  return (
    <span className={`date-field ${className ?? ''}`.trim()}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="DD/MM/YYYY"
        id={id}
        aria-label={label}
        required={required}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          commit(e.target.value);
        }}
        onBlur={() => setText(value ? fmtDateNumeric(value) : '')}
      />
      {/*
        The calendar glyph and the real date input, stacked. The glyph is decoration; the input is
        invisible but genuinely touchable and sits on top of it, so a tap lands on a date field
        rather than on a button that has to persuade one to open.
      */}
      <span className="date-pick">
        <CalendarDays size={16} aria-hidden="true" />
        <input
          ref={native}
          type="date"
          className="native"
          aria-label={t('date_open_calendar')}
          value={value}
          min={min}
          max={max}
          onClick={(e) => openPicker(e.currentTarget)}
          onChange={(e) => {
            const v = e.target.value;
            if (v && inRange(v)) onChange(v);
          }}
        />
      </span>
    </span>
  );
}
